import moment from 'moment';

/**
 * The profile timezone is saved as "<IANA name> (UTC±X)" (e.g. "Asia/Jerusalem (UTC+2)") - the label holds the
 * offset of the day it was chosen. The offset is taken from the IANA name for the date being formatted, so daylight
 * saving applies (Jerusalem: +2 in winter, +3 in summer); the label's fixed offset is only a fallback, for values
 * that are not a timezone name ("UTC+3").
 */

/** The IANA name of the saved timezone ("Asia/Jerusalem (UTC+2)" -> "Asia/Jerusalem"), null when it is not one */
const ianaTimezone = (tzString: string): string | null => {
  const name = tzString.split(' (')[0].trim();
  if (!name || /^UTC[+-]/i.test(name)) {
    return null;
  }
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: name });
    return name;
  } catch {
    return null;
  }
};

/** The UTC offset in minutes of an IANA timezone at `date` (daylight saving included), null when unknown */
const ianaOffsetMinutes = (timeZone: string, date: Date): number | null => {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      timeZoneName: 'longOffset',
    } as Intl.DateTimeFormatOptions).formatToParts(date);
    const value = parts.find((part) => part.type === 'timeZoneName')?.value; // "GMT+03:00" / "GMT"
    if (!value) {
      return null;
    }
    if (value === 'GMT' || value === 'UTC') {
      return 0;
    }
    const match = value.match(/(?:GMT|UTC)([+-])(\d{1,2})(?::?(\d{2}))?/);
    if (!match) {
      return null;
    }
    const sign = match[1] === '+' ? 1 : -1;
    return sign * (Number(match[2]) * 60 + Number(match[3] ?? 0));
  } catch {
    return null;
  }
};

/** The fixed offset written in the label ("... (UTC+2)" / "UTC+3"), in minutes */
const parseTimezoneToMinutes = (tzString: string): number => {
  if (!tzString) {
    return 0;
  }

  // Extract from (UTC±X) pattern (e.g., "Africa/Johannesburg (UTC+2)")
  const utcMatch = tzString.match(/\(UTC([+-])(\d+(?:\.\d+)?)\)/);
  if (utcMatch) {
    const sign = utcMatch[1] === '+' ? 1 : -1;
    return sign * parseFloat(utcMatch[2]) * 60;
  }

  // Fallback for pure UTC format (e.g., "UTC+3")
  const cleanTz = tzString.toUpperCase().replace('UTC', '').trim();
  if (cleanTz && cleanTz !== '') {
    const offsetMatch = cleanTz.match(/([+-])(\d+(?:\.\d+)?)/);
    if (offsetMatch) {
      const sign = offsetMatch[1] === '+' ? 1 : -1;
      return sign * parseFloat(offsetMatch[2]) * 60;
    }
  }

  return 0;
};

/** The offset of the saved timezone at `date`: from its IANA name when it has one, else the label's fixed offset */
const timezoneOffsetMinutes = (tzString: string, date: Date): number => {
  const iana = ianaTimezone(tzString);
  const offset = iana ? ianaOffsetMinutes(iana, date) : null;
  return offset ?? parseTimezoneToMinutes(tzString);
};

/** True when localStorage has a non-empty profile timezone (e.g. backoffice user settings). */
export function isActiveTimezoneConfigured(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  return Boolean(localStorage.getItem('activeTz')?.trim());
}

// Store original format function
const originalFormat = moment.prototype.format;

/**
 * Create new function formatTZ() to automatically apply user's configured timezone from localStorage
 * This way ANY component using moment().formatTZ() will automatically get timezone conversion
 */
moment.fn.formatTZ = function (format: string): string {
  // Fallback to original format
  const fallbackResult = originalFormat.call(this, format);

  try {
    // Get timezone from localStorage (already saved there when user updates profile)
    const timezone = typeof window !== 'undefined' ? localStorage.getItem('activeTz') : null;
    if (!timezone) {
      return fallbackResult;
    }

    // a named timezone is applied also at offset 0 (UTC / London in winter); a bare "UTC+0" keeps the local time
    const isNamed = !!ianaTimezone(timezone);
    const offsetMinutes = timezoneOffsetMinutes(timezone, this.toDate());
    if (offsetMinutes == 0 && !isNamed) {
      return fallbackResult;
    }

    return this.clone().utc().utcOffset(offsetMinutes).format(format);
  } catch (error) {
    // Silently fall back to original format if localStorage access fails
    return fallbackResult;
  }
};

/**
 * Also keep forceFormatTZ() method for explicit timezone override when needed
 */
moment.fn.forceFormatTZ = function (format: string, timezone: string = 'UTC'): string {
  const offsetMinutes = timezoneOffsetMinutes(timezone, this.toDate());
  return this.clone().utc().utcOffset(offsetMinutes).format(format);
};

// TypeScript declarations
declare global {
  namespace moment {
    interface Moment {
      formatTZ(format: string): string;
      forceFormatTZ(format: string, timezone: string): string;
    }
  }
}

const offsetLabel = (offsetMinutes: number) => {
  const sign = offsetMinutes < 0 ? '-' : '+';
  const absolute = Math.abs(offsetMinutes);
  return `UTC ${sign}${String(Math.floor(absolute / 60)).padStart(2, '0')}:${String(absolute % 60).padStart(2, '0')}`;
};

/** UTC offset label for tooltip footers - today's offset of activeTz when set, else the browser's. */
export function getConfiguredTimezoneOffsetLabel(): string {
  const timezone = isActiveTimezoneConfigured() ? localStorage.getItem('activeTz')?.trim() : null;
  if (timezone && (ianaTimezone(timezone) || /UTC[+-]/i.test(timezone))) {
    return offsetLabel(Math.round(timezoneOffsetMinutes(timezone, new Date())));
  }
  return offsetLabel(-new Date().getTimezoneOffset());
}

export default moment;
